<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	import { onMount, createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		ActionsContainer,
		IconButton,
		HeatingCoolingCard,
		HeatingCoolingCardV2,
		Label,
		LabelV2,
	} from '@Shelly/shelly-design-system';
	import { postReq } from '../../../services/http';
	import { getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { rooms } from 'src/stores/lists/rooms';
	import { dashboards } from 'src/stores/features/dashboards';
	import { edit } from 'src/stores/ui/edit_lists';
	import { longPressEditAvailable } from 'src/stores/features/dev/long-press-edit';
	import { buildListPressHoldOptions } from 'src/helpers/long_press_edit_helpers';
	import { dndHandleClick } from '../list-helpers';
	import ControlEnableTempScheme from '../../Controls/TemperatureSchemes/ControlEnableTempScheme.svelte';
	import TempSchemeChart from '../../Chart/ChartJS/TempSchemeChart.svelte';
	import { translateThermostat } from '../../TemperatureScheme/helpers/temp_scheme_to_ui';
	import type { IChartTempSchedule } from 'src/types/TemperatureSchemeType';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { updateItemStoreAfterPulse } from 'src/helpers/add-entity-helpers';

	const dispatch = createEventDispatcher();

	export let item: any = {};
	export let missingDevice = false;
	export let disableEditBtn = false;

	let data: IChartTempSchedule = translateThermostat(item);

	$: thermostatStatusStore = getDeviceStatus(item._id);
	$: _thermostat_data = $thermostatStatusStore?.thermostat;
	let room_name = $rooms[item?._meta?.room]?.name || $dashboards[item?._meta?.room]?.name || 'Unknown';

	$: pressHoldOptions = buildListPressHoldOptions($edit, $longPressEditAvailable, {
		enabled: !disableEditBtn,
		onEntered: () => dispatch('handle-drag-start'),
	});

	onMount(() => {
		/**It is because when we create a new thermostat and come here
     device statuses don't know about the new one
     if the user enabled the new one (we don't have statues for it)
     it is not visible in the room page
    soo ask for status and update store every time when don't know for thermostat
    */
		if (!_thermostat_data && item._id !== undefined) {
			postReq(`/device/status?id=${item._id}&virtual=1`, {})
				.then((res) => {
					if (res.isok) {
						devices_status.update_device_status({
							id: item._id,
							msg: res.data?.device_status,
						});
					}
				})
				.catch((err) => console.log('err on getting thermostat status ', err));
		}
	});

	const onHandleDragStart = (e: MouseEvent | TouchEvent) => {
		if (disableEditBtn) return;
		dndHandleClick(e, $edit);
		dispatch('handle-drag-start', e);
	};

	const onHandleDragEnd = (e: MouseEvent | TouchEvent) => {
		dndHandleClick(e, $edit);
		dispatch('handle-drag-end', e);
	};

	const onHandleCardEdit = (e: MouseEvent | TouchEvent) => {
		e.stopPropagation();
		dispatch('edit', e);
	};

	const onHandleCardDelete = (e: MouseEvent | TouchEvent) => {
		e.stopPropagation();
		dispatch('delete', e);
	};

	$: if (item?._meta?.new && $edit) {
		// Remove pulse when added new item and we are in edit mode
		updateItemStoreAfterPulse(item, 'thermostats');
	}
</script>

{#if $showNewLayout}
	<HeatingCoolingCardV2
		style="overflow:unset"
		title={item._meta.name}
		icon={`fat ${item?._meta.type === 'heat' ? 'fa-temperature-hot' : 'fa-temperature-frigid'}`}
		editMode={$edit}
		selected={item.id === $sidePanelStateCombined?.id}
		hideLeftSlot={!missingDevice}
		isFocusable={false}
		{pressHoldOptions}
		on:click
		on:startDrag={(e) => onHandleDragStart(e.detail.event)}
		on:endDrag={(e) => onHandleDragEnd(e.detail.event)}
		pulseAnimation={item?._meta?.new}
		on:pulse-animation-end={() => {
			updateItemStoreAfterPulse(item, 'thermostats');
		}}
	>
		<LabelV2
			slot="leftBadge"
			value={$format('missing_device_label', { default: 'Missing Device' })}
			backgroundColor="--color-warning"
			icon="fal fa-warning"
			iconColor="text-base"
			size="xs"
			noRadiusType="second"
		/>

		<svelte:fragment slot="right">
			<ControlEnableTempScheme id={item._id} />
		</svelte:fragment>
		<svelte:fragment slot="main">
			<TempSchemeChart {data} editMode={$edit} />
		</svelte:fragment>
	</HeatingCoolingCardV2>
{:else}
	<HeatingCoolingCard
		style="overflow:unset"
		title={item._meta.name}
		icon={`fat ${item?._meta.type === 'heat' ? 'fa-temperature-hot' : 'fa-temperature-frigid'}`}
		editMode={$edit}
		on:click
		selected={item.id === $sidePanelStateCombined?.id}
		hideLeftSlot={!missingDevice}
		isFocusable={false}
		pulseAnimation={item?._meta?.new}
		on:pulse-animation-end={() => {
			updateItemStoreAfterPulse(item, 'thermostats');
		}}
	>
		<Label
			slot="leftBadge"
			value={$format('missing_device_label', { default: 'Missing Device' })}
			type="warning"
			icon="fas fa-warning"
			iconColor="text-base"
			size="xs"
			noRadiusType="second"
		/>

		<svelte:fragment slot="right">
			{#if $edit}
				<ActionsContainer orientation="horizontal">
					<IconButton
						on:mousedown={onHandleDragStart}
						on:touchstart={onHandleDragStart}
						on:mouseup={onHandleDragEnd}
						on:touchend={onHandleDragEnd}
						icon="fas fa-up-down-left-right"
						slot="first"
						dndHandle
						disabled={disableEditBtn}
						class={classMap({ 'cursor-not-allowed': disableEditBtn })}
						aria-label={$format('aria_drag_thermostat', {
							default: 'Drag thermostat',
						})}
					/>
					<IconButton
						icon="fas fa-pen"
						slot="second"
						on:click={onHandleCardEdit}
						aria-label={$format('aria_edit_thermostat', {
							default: 'Edit thermostat',
						})}
					/>
					<IconButton
						icon="fas fa-trash"
						variant="danger"
						slot="third"
						on:click={onHandleCardDelete}
						aria-label={$format('aria_delete_thermostat', {
							default: 'Delete thermostat',
						})}
					/>
				</ActionsContainer>
			{:else}
				<ControlEnableTempScheme id={item._id} />
			{/if}
		</svelte:fragment>
		<svelte:fragment slot="main">
			<TempSchemeChart {data} editMode={$edit} />
		</svelte:fragment>
	</HeatingCoolingCard>
{/if}
